<template>
	<div id="app">
		<ProgressDialog
            v-if="progressDialogVisible && this.$route.path !== '/home/loading'"
			:expansion="progressDialogParam.visible"
			:orderList="orderList"
			@updateExpansion="updateExpansion"
		>
		</ProgressDialog>
		<router-view :class="routerViewClass" />
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ProgressDialog from '@/components/progress_dialog'
import { getMixOrders as getMemberMixOrders } from './api/memberOrder'
import { getMixOrders as getDistributorMixOrders } from './api/distributorOrder'
import { OrderState } from './const/order'

export default {
	name: 'App',
	components: {
		ProgressDialog
	},
	sockets: {
		sendMessage: function (payload) {
			this.appendMessage(payload)
			// @todo 補上通知
		},
		connect() {
			this.setReconnect(false)
		}
	},
	data () {
		return {
			mixOrdersParam: {
				offset: 0,
				limit: 100,
				status: `${OrderState.PAY},${OrderState.RECEIPT},${OrderState.OCCUPY},${OrderState.DELIVER},${OrderState.SEND}`
			},
			progressDialogParam: {
				visible: false,
				selectedIndex: 0,
				unreadCount: 0
			},
			path: '',
			orderList: [],
			requestOrderListTimer: null,
			timers: {
				reconnectionTimer: undefined
			}
		}
	},
	computed: {
		...mapGetters(['getReconnectState']),
		...mapState({
			config: (state) => state.config.config,
			isMemberAuthorized: (state) => state.member.token,
			isDistributorAuthorized: (state) => state.distributor.token,
			rooms: (state) => state.chatroom.rooms
		}),
		routerViewClass () {
			return {
				'progress-dialog-margin': this.progressDialogVisible
			}
		},
		isAuthorized () {
			return !!(this.isMemberAuthorized || this.isDistributorAuthorized)
		},
		roomClients () {
			if (this.isDistributorAuthorized) {
				return ['admin', 'member']
			} else if (this.isMemberAuthorized) {
				return ['admin', 'distributor']
			} else {
				return []
			}
		},
		progressDialogVisible () {
			return this.orderNum > 0 && this.allowRequestOrderListPages && !this.currentPath.match(/\/distributor\//)
		},
		allowRequestOrderListPages () {
			const allowPages = [
				'HomeIndex',
				'HomeWaimaiIndex',
				'MemberO2oErrand',
				'MemberO2oErrandSend',
				'MemberOrderList',
				'MemberIndex',
				'HomeStorelist',
				'DistributorHome',
				'DistributorErrand',
				'DistributorErrandOrderDetail',
				'DistributorO2oErrandOrderDetail',
				'DistributorMap',
				// 'DianShuJiLu',
				// 'DistributorUser
			]
			return allowPages.some((v) => v === this.currentRouteName)
		},
		currentPath () {
			return this.$route.path
		},
		currentRouteName () {
			return this.$route.name
		},
		orderNum: {
			get () {
				if (this.isMemberAuthorized) {
					return this.$store.state.member.orderNum
				} else {
					return 0
				}
			},
			set (value) {
				if (this.isMemberAuthorized) {
					this.$store.commit('orderNumUpdate', value)
				}
			}
		}
	},
	watch: {
		currentRouteName: {
			handler: function (val, oldval) {
				if (this.isAuthorized === null || !this.allowRequestOrderListPages) {
					this.clearRequestOrdersTimer()
					return
				}

				if (typeof this.requestOrderListTimer === 'undefined') {
					this.updateOnGoingOrders()
					const _this = this
					this.requestOrderListTimer = setInterval(function () {
						_this.updateOnGoingOrders()
					}, 10000)
				}
			},
			immediate: true
		}
	},
	created: function () {
		history.pushState({}, "", location.href)

		this.timers.reconnectionTimer = setInterval(() => {
			if (this.isMemberAuthorized || this.isDistributorAuthorized) {
				if (!this.$socket.connected) {
					this.setReconnect(true)
					this.$socket.connect()
				} 
			}
		}, 2000)
	},
	beforeDestroy: function () {
		this.clearRequestOrdersTimer()
		clearInterval(this.timers.reconnectionTimer)
	},
	methods: {
		...mapActions([
			'joinMultipleRooms',
			'joinRoom',
			'leaveRoom',
			'sendMessage',
			'appendMessage',
			'initialChatData',
			'clearChatData',
			'setReconnect'
		]),
		/**
		 * When client is authorized will update on going orders.
		 */
		async updateOnGoingOrders () {
			if (!this.isAuthorized) {
				if (Object.keys(this.rooms).length > 0) {
					this.clearChatData()
				}
				return
			}
			if (window.nativePostMessage) {
				window.nativePostMessage({
					type: 'set-access-token',
					access_token: this.isMemberAuthorized || this.isDistributorAuthorized,
					api_path: process.env.VUE_APP_API_HOST
				})
			}

			try {
				let res = {
					code: undefined
				}
				if (this.isDistributorAuthorized) {
					res = await getDistributorMixOrders(this.mixOrdersParam)
				} else {
					res = await getMemberMixOrders(this.mixOrdersParam)
				}
				if (res.code !== 10000) {
					throw new Error(res.msg)
				}
				console.log('=== UPDATE ON GOING ORDERS SUCCESSFUL ===', res)

				const originOrderIds = this.orderList.map((order) => {
					return order.errand_order_type === 'buy'
						? order.order_id
						: order.o2o_errand_order_id
				})

				const newOrderIds = res.result.map((order) => {
					return order.errand_order_type === 'buy'
						? order.order_id
						: order.o2o_errand_order_id
				})

				const intersection = originOrderIds.filter(
					(x) => !newOrderIds.includes(x)
				)

				this.orderNum = res.result.length

				if (this.isDistributorAuthorized || this.orderNum > 0) {
					this.orderList = res.result
					if (
						!this.getReconnectState &&
						originOrderIds.length > 0 &&
						intersection.length === 0
					) {
						return
					}
					this.orderList.forEach((order) => {
						this.joinMultipleRooms({
							clients: this.roomClients,
							order: order
						})
					})
				} else {
					this.hiddenProgressDialog(0, 0)
					this.orderList.length = 0
				}
			} catch (err) {
				console.error('=== UPDATE ON GOING ORDERS FAILED ===', err)
			}
		},
		/**
		 * Hidden progress dialog.
		 */
		hiddenProgressDialog (orderIndex, unreadCount) {
			this.updateProgressDialogParam({
				visible: false,
				selectedIndex: orderIndex,
				unreadCount: unreadCount
			})
			document.getElementById('app').style.overflow = 'scroll'
		},
		/**
		 * Open progress dialog.
		 */
		openProgressDialog () {
			this.updateProgressDialogParam({ visible: true })
			document.getElementById('app').style.overflow = 'hidden'
		},
		/**
		 * Update progress dialog param.
		 */
		updateProgressDialogParam (payload) {
			this.progressDialogParam = Object.assign(
				this.progressDialogParam,
				payload
			)
		},
		/**
		 * Clear request orders timer
		 */
		clearRequestOrdersTimer () {
			this.requestOrderListTimer = clearInterval(this.requestOrderListTimer)
		},
		updateExpansion (value) {
			this.updateProgressDialogParam({ visible: value })
		}
	}
}
</script>
